import { useTranslate } from "@tolgee/react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "#/components/ui/button";
import { useDeposit } from "#/hooks/use-trading-actions";
import { useUnifiedBalance } from "#/hooks/use-unified-balance";
import { useWallet } from "#/hooks/use-wallet";
import { useWalletBalance } from "#/hooks/use-wallet-balance";
import { formatWalletAddress } from "#/lib/formatters";
import { useTradeAccount } from "../../hooks/use-trade-data.ts";
import { DepositDialog } from "../order-form/deposit-dialog.tsx";
import { DepositWarningIndicator } from "../order-form/deposit-warning-indicator.tsx";
import { useDepositInTransit } from "../order-form/hooks/use-deposit-in-transit.ts";
import { TradePanel } from "../trade-panel.tsx";
import { AccountInfoActions } from "./account-info-actions.tsx";
import { AccountInfoSection } from "./account-info-section.tsx";
import { useAccountInfo } from "./hooks/use-account-info.ts";

const EMPTY_ACCOUNT_STATE = {
	crossMarginSummary: {
		accountValue: "0",
		totalMarginUsed: "0",
		totalNtlPos: "0",
	},
	crossMaintenanceMarginUsed: "0",
};

export function AccountInfo() {
	const { t } = useTranslate();
	const wallet = useWallet();
	const { accountState, positions } = useTradeAccount();
	const { balance: walletBalance } = useWalletBalance();
	const { isUnified, isDexAbstraction, spotUsdc, spotUsdcHold } =
		useUnifiedBalance();
	const deposit = useDeposit();
	const depositInTransit = useDepositInTransit();

	const info = useAccountInfo({
		accountState: accountState ?? EMPTY_ACCOUNT_STATE,
		positions,
		walletBalance,
		isUnified,
		isDexAbstraction,
		spotUsdc,
		spotUsdcHold,
	});

	if (!wallet.isConnected) {
		return (
			<TradePanel className="overflow-hidden shadow-none">
				<div className="trade-panel-handle h-1.5 shrink-0 cursor-grab" />
				<div className="flex min-h-0 flex-1 items-center justify-center px-4 py-8">
					{wallet.isAuthenticated ? (
						<span className="text-label2 text-muted-foreground">
							{t("state.loading-wallet")}
						</span>
					) : (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => wallet.connect("trade_cta")}
							disabled={!wallet.isReady}
							className="text-muted-foreground"
						>
							{t("account.connect-prompt")}
						</Button>
					)}
				</div>
			</TradePanel>
		);
	}

	// Both balances zero — fresh user. A crediting deposit counts as funds:
	// a full-wallet deposit zeroes the wallet balance while the bridge credit
	// is still in flight, and regressing to "get started" would also unmount
	// the open DepositDialog below.
	const hasFunds =
		info.tradingBalance > 0 || info.walletBalance > 0 || depositInTransit;
	if (!hasFunds) {
		return (
			<TradePanel className="overflow-hidden shadow-none">
				<div className="trade-panel-handle h-1.5 shrink-0 cursor-grab" />
				<div className="flex min-h-0 flex-1 flex-col gap-3 px-4 py-4">
					<p className="text-label1-bold text-trade-text-strong">
						{t("account.get-started")}
					</p>
					<p className="text-label2 text-trade-text-secondary">
						{t("account.wallet-empty")}
					</p>
					<div className="flex items-center justify-between gap-2 rounded-xl bg-trade-surface-inset px-3 py-2">
						<span className="text-label2-medium text-trade-text">
							{wallet.address ? formatWalletAddress(wallet.address) : ""}
						</span>
						<Button
							variant="ghost"
							size="icon-xs"
							onClick={() =>
								wallet.address &&
								(navigator.clipboard.writeText(wallet.address),
								toast.success(t("actions.copied")))
							}
							className="text-trade-text-secondary hover:text-trade-text-strong hover:bg-transparent"
							aria-label={t("actions.copy-address-short")}
						>
							<Copy className="h-3.5 w-3.5" />
						</Button>
					</div>
				</div>
			</TradePanel>
		);
	}

	// Has wallet USDC but no trading balance (or a deposit is crediting)
	if (
		info.tradingBalance === 0 &&
		(info.walletBalance > 0 || depositInTransit)
	) {
		return (
			<TradePanel className="overflow-hidden shadow-none">
				<div className="trade-panel-handle h-1.5 shrink-0 cursor-grab" />
				<div className="flex min-h-0 flex-1 flex-col gap-3 px-4 py-4">
					<p className="flex items-center gap-1.5 text-label1-bold text-trade-text-strong">
						{/* Spinner suppressed while crediting — the order form's
						    available-funds row on the same screen already shows one */}
						{!depositInTransit && <DepositWarningIndicator />}
						{t("account.almost-there")}
					</p>
					<AccountInfoSection
						title={t("account.balances")}
						metrics={info.balanceMetrics}
					/>
					<p className="text-label2 text-trade-text-secondary">
						{t("account.deposit-prompt")}
					</p>
					<DepositDialog onConfirm={(amount) => deposit.mutateAsync(amount)}>
						<Button className="rounded-xl bg-trade-interactive px-4 py-2.5 text-trade-text-strong hover:bg-trade-interactive-hover">
							{t("account.deposit-to-account")}
						</Button>
					</DepositDialog>
				</div>
			</TradePanel>
		);
	}

	// Normal trading view
	return (
		<TradePanel className="overflow-hidden shadow-none">
			<div className="trade-panel-handle h-1.5 shrink-0 cursor-grab" />
			<div className="flex min-h-0 flex-1 flex-col gap-4 px-4 py-4">
				<AccountInfoActions />
				<AccountInfoSection
					title={t("account.balances")}
					metrics={info.balanceMetrics}
				/>
				<AccountInfoSection
					title={t("account.trading-overview")}
					metrics={info.overviewMetrics}
				/>
			</div>
		</TradePanel>
	);
}
